/**
 * `search-judgements`, `search-boosts`, `search-stability` — FR-26, FR-27,
 * NFR-13, O-6 · CLM-39 (Phase 5.2, D-B10).
 *
 * The **read side of the search feedback loops**. The writes are three members
 * of the `manage-clause` union (`record_search_judgement`,
 * `retract_search_judgement`, `set_clause_boost`); these three reads let a
 * reviewer see what has been recorded, what it weighs today, and whether the
 * ranking has held still for a query since.
 *
 * ## Corpus-level, gated `clause:review_match`
 *
 * `clause_search_feedback`, `clause_library` and `clause_search_log` carry no
 * party or contract column, and FR-17 grants the library to every authorised
 * user, so nothing here is scoped and `page.total_in_scope` is the corpus count
 * of the same rows (the `clauses` reasoning). The gate is `clause:review_match`
 * — admin and Legal — because a judgement ledger is a work list, not a report
 * (G19).
 *
 * ## Stability is a comparison of logged outcomes, never a re-ranking
 *
 * O-6: the same query must rank the same way for the same stored rows. The
 * stability read compares `clause_search_log` rows for one query text and marks
 * a pair `tuned_since` when a feedback, library or alias row changed between
 * the two runs — derived from those tables' own timestamps, not from
 * `audit_log`. It is a **hint, not a proof**: a re-embed, a different query
 * vector for identical text, or a τ/λ_f change leave no row it can see, so the
 * copy says "tuned since" / "no tune recorded", never "explained".
 */
import { z } from 'zod';
import { ClauseCodeSchema, IsoTimestampUtcSchema, OpaqueIdSchema } from '../primitives.js';
import { PageInfoSchema, PageRequestSchema } from '../pagination.js';
import { ClauseSearchJudgementSchema, LibraryClauseSchema } from '../domain/clause.js';
/* -------------------------------------------------------------------------- */
/* GET /v1/search/judgements                                                   */
/* -------------------------------------------------------------------------- */
export const SearchJudgementListRequestSchema = z
    .object({
    /**
     * Only judgements recorded against a superseded clause version. Server
     * default `false`: the ledger lists everything; the re-confirmation queue
     * is `quality-stale-judgements` (CLM-40).
     */
    stale_only: z.boolean().optional(),
    clause_code: ClauseCodeSchema.optional(),
    page: PageRequestSchema.optional(),
})
    .strict();
export const SearchJudgementListResponseSchema = z.object({
    /** Stale first, then newest first — the same order the statement uses for its keyset. */
    items: z.array(ClauseSearchJudgementSchema),
    page: PageInfoSchema,
    /** Corpus-level counters over the active judgements (the filter's `clause_code` applies). */
    totals: z.object({
        active: z.number().int().min(0),
        stale: z.number().int().min(0),
    }),
});
/* -------------------------------------------------------------------------- */
/* GET /v1/search/boosts                                                       */
/* -------------------------------------------------------------------------- */
export const SearchBoostListRequestSchema = z
    .object({
    page: PageRequestSchema.optional(),
})
    .strict();
export const SearchBoostListResponseSchema = z.object({
    /** Current clauses with `boost_weight ≠ 0`, ordered by `clause_code`; each carries its `boost_reason`. */
    items: z.array(LibraryClauseSchema),
    page: PageInfoSchema,
    totals: z.object({
        /** `boost_weight > 0` — pinned up. */
        boosted: z.number().int().min(0),
        /** `boost_weight < 0` — buried. */
        buried: z.number().int().min(0),
    }),
});
/* -------------------------------------------------------------------------- */
/* POST /v1/search/stability                                                   */
/* -------------------------------------------------------------------------- */
export const SEARCH_STABILITY_DEFAULT_LIMIT = 10;
export const SearchStabilityRequestSchema = z
    .object({
    /** The query as typed — matched case- and whitespace-insensitively against `clause_search_log.query_text`. */
    query_text: z.string().trim().min(1).max(400),
    /** How many logged runs to compare, newest first. Server default 10. */
    limit: z.number().int().min(2).max(50).optional(),
})
    .strict();
/** One logged run of the query — `clause_search_log` (0024), the actor deliberately omitted. */
export const SearchStabilityRunSchema = z.object({
    search_id: OpaqueIdSchema,
    occurred_utc: IsoTimestampUtcSchema,
    effective_query: z.string().min(1),
    /** `null` for a zero-result run, or when the top clause has since been deleted. */
    top_clause_code: ClauseCodeSchema.nullable(),
    top_score: z.number().nullable(),
    result_count: z.number().int().min(0),
    aliases_applied: z.array(z.object({
        alias_term: z.string().min(1).max(120),
        canonical_term: z.string().min(1).max(200),
    })),
});
/** A newer run compared with the run before it. */
export const SearchStabilityPairSchema = z.object({
    /** The older run. */
    from_search_id: OpaqueIdSchema,
    /** The newer run. */
    to_search_id: OpaqueIdSchema,
    /** `top_clause_code` or `round(top_score, 4)` differs between the two. */
    drift: z.boolean(),
    /** A feedback, library or alias row changed in `(from.occurred_utc, to.occurred_utc]` — a hint, not a proof. */
    tuned_since: z.boolean(),
});
export const SearchStabilityResponseSchema = z.object({
    query_text: z.string().min(1).max(400),
    /** Newest first. */
    runs: z.array(SearchStabilityRunSchema),
    /** One per adjacent pair of `runs`, newest pair first; empty with fewer than two runs. */
    pairs: z.array(SearchStabilityPairSchema),
    /** Pairs with `drift && !tuned_since`. */
    unexplained_drift_count: z.number().int().min(0),
});
//# sourceMappingURL=search-quality.js.map